<template>
  <div v-loading="isLoading" class="invoice-application" style="height: calc(100% - 76px);">
    <!-- 搜索条件区域 -->
    <!-- 撮合主体 -->
    <div v-if="source === 'sass-match'" class="w-[300px] p-[16px_16px_0_16px]">
      <el-select
        v-model="params.companyId"
        placeholder="企业名称"
        filterable
        :teleported="false"
        @change="changeCompany"
      >
        <template #label>
          <div class="flex items-center gap-x-2">
            <el-tag v-if="isSass && companyIsPendingApply" type="warning">
              待申请
            </el-tag>
            <div :class="['text-left', 'truncate', companyIsPendingApply ? 'w-[165px]' : 'w-[215px]']">
              {{ companyList.find(item => item.id === params.companyId)?.companyName }}
            </div>
          </div>
        </template>
        <el-option
          v-for="(v, idx) in companyList"
          :key="idx"
          :label="v.companyName"
          :value="v.id"
        >
          <template #default>
            <div class="flex items-center gap-x-2">
              <el-tag v-if="isSass && v.havePendingApply === 1" type="warning">
                待申请
              </el-tag>
              <div :class="['text-left', 'truncate', 'max-w-[235px]', v.havePendingApply === 1 ? '' : 'indent-[65px] max-w-[300px]', isCompanyEveryPending ? '!indent-0 max-w-[300px]' : '']">
                {{ v.companyName }}
              </div>
            </div>
          </template>
        </el-option>
      </el-select>
    </div>

    <!-- 众包主体 -->
    <div v-if="source === 'sass-crowd'" class="w-[300px] p-[16px_16px_0_16px]">
      <el-select
        v-model="params.serverId"
        placeholder="服务主体名称"
        filterable
        :teleported="false"
        @change="changeCompany"
      >
        <template #label>
          <div class="flex items-center gap-x-2">
            <el-tag v-if="isSass && serverIsPendingApply" type="warning">
              待申请
            </el-tag>
            <div :class="['text-left', 'truncate', serverIsPendingApply ? 'w-[165px]' : 'w-[215px]']">
              {{ serverList.find(item => item.id === params.serverId)?.serverName }}
            </div>
          </div>
        </template>
        <el-option
          v-for="(v, idx) in serverList"
          :key="idx"
          :label="v.serverName"
          :value="v.id"
        >
          <template #default>
            <div class="flex items-center gap-x-2">
              <el-tag v-if="isSass && v.havePendingApply === 1" type="warning">
                待申请
              </el-tag>
              <div :class="['text-left', 'truncate', 'max-w-[235px]', v.havePendingApply === 1 ? '' : 'indent-[65px] max-w-[300px]', isServerEveryPending ? '!indent-0 max-w-[300px]' : '']">
                {{ v.serverName }}
              </div>
            </div>
          </template>
        </el-option>
      </el-select>
    </div>

    <!-- 额度 -->
    <invoice-application-money 
      v-if="!isHnOfflineInvoice"
      :all-warning-total="allWarningTotal" 
      :tax-source-quota="taxSourceQuota" 
      @handle-exception="handleException"
    />

    <!-- 众包开票申请 -->
    <invoice-application-table
      v-if="isCrowd"
      :source="source"
      :is-hn-offline-invoice="isHnOfflineInvoice"
      :server-id="params?.serverId"
      @confirm="initApi()"
    />

    <!-- 撮合开票申请 -->
    <invoice-application-apply
      v-else
      :invoice-list="invoiceList"
      :company-id="params?.companyId"
      :server-id="params?.serverId"
      :source="source"
      @handle-exception="handleException"
      @confirm="initApi()"
    />
  </div>
</template>

<script setup lang="ts">
import InvoiceApplicationMoney from './InvoiceApplicationMoney.vue';
import InvoiceApplicationApply from './InvoiceApplicationApply.vue';
import InvoiceApplicationTable from './InvoiceApplicationTable.vue';
import { reactive, ref, PropType, computed, watch } from 'vue';
import { ICrowdCompanyItem, ITaxSourceQuotaDetail, ICrowdServerItem, IInvoiceApplicationParams, IInvoiceSubjectAndTaxItem, EHnInvoiceType } from '@core/main/types';
import { useRouter } from 'vue-router';
import { ElMessage } from 'element-plus';
import { queryInvoiceSubjectAndTax, queryOrdersCheck, queryTaxSourceQuota } from '@core/main/api';
import { isNull } from '@core/tools';

defineOptions({ name: 'InvoiceApplication' }); // 众包灵工 - 发票申请

const props = defineProps({
  source: {
    type: String as PropType<'server' | 'company' | 'sass-crowd' | 'sass-match'>,
    default: 'server',
  },
  companyList: {
    type: Array as PropType<ICrowdCompanyItem[]>,
    default: () => [],
  },
  serverList: {
    type: Array as PropType<ICrowdServerItem[]>,
    default: () => [],
  },
  isPermission: {
    type: Boolean,
    default: false,
  },
});

const router = useRouter();

const orderLoading = ref(false); // 异常订单loading
const taxLoading = ref(false); // 额度loading
const invoiceLoading = ref(false); // 开票科目列表loading
const invoiceList = ref([] as IInvoiceSubjectAndTaxItem[]);
const taxSourceQuota = ref({} as ITaxSourceQuotaDetail);
const allWarningTotal = ref(0);
const params = reactive<IInvoiceApplicationParams>({});

const isLoading = computed(() => orderLoading.value || taxLoading.value || invoiceLoading.value);
const isCrowd = computed(() => props.source === 'sass-crowd' || props.source === 'server');
const isSass = computed(() => props.source === 'sass-match' || props.source === 'sass-crowd');
const companyIsPendingApply = computed(() => props.companyList.find((v) => params.companyId === v.id)?.havePendingApply === 1);
const serverIsPendingApply = computed(() => props.serverList.find((v) => params.serverId === v.id)?.havePendingApply === 1);
const isCompanyEveryPending = computed(() => props.companyList.every((v) => v.havePendingApply === 0));
const isServerEveryPending = computed(() => props.serverList.every((v) => v.havePendingApply === 0));
const isHnOfflineInvoice = computed(() => isCrowd.value && isSass.value && props.serverList?.find((v) => v?.id === params.serverId)?.hnInvoiceType === EHnInvoiceType.OFFLINE);

function changeCompany() {
  initApi();
}

function handleException(invoiceSubjectId?: number) {

  router.push({
    name: 'BrokerAbnormalOrder',
    query: {
      invoiceSubjectId: isNull(invoiceSubjectId) ? undefined : invoiceSubjectId,
      companyId: params.companyId, 
    }, 
  });
}
/**
 * @function 获取额度
*/
async function getTaxSourceQuota() {
  taxLoading.value = true;
  try {
    const businessId = isCrowd.value ? params.serverId : params.companyId;
    const businessType = isCrowd.value ? 3 : 2;

    taxSourceQuota.value = await queryTaxSourceQuota({
      businessId,
      businessType, 
    });
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
  taxLoading.value = false;
}

/**
 * @function 获取开票科目列表
*/
async function getInvoiceSubjectAndTax() {
  invoiceLoading.value = true;
  try {
    invoiceList.value = await queryInvoiceSubjectAndTax(isCrowd.value ? params.serverId : params.companyId, isCrowd.value);
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
  invoiceLoading.value = false;
}

/**
 * @function 获取异常订单
*/
async function getOrdersCheck() {
  orderLoading.value = true;
  try {
    const { warningOrderCount } = await queryOrdersCheck({ companyId: params.companyId });

    allWarningTotal.value = warningOrderCount;
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
  orderLoading.value = false;
}

/**
 * @function 初始化接口 切换企业/服务商调用
*/
function initApi() {
  if (isHnOfflineInvoice.value) {
    getTaxSourceQuota();
  }

  if (isCrowd.value) {
    allWarningTotal.value = 0;
  } else {
    getOrdersCheck();
    getInvoiceSubjectAndTax();
  }
}

watch(
  () => props.isPermission,
  (val) => {
    if (!val) {
      params.companyId = isCrowd.value ? undefined : Number(props.companyList?.[0]?.id);
      params.serverId = isCrowd.value ? Number(props.serverList?.[0]?.id) : undefined;

      initApi();
    }
  },
  { immediate: true },
);
</script>

<style lang="scss" scoped>
.invoice-application {
  @apply w-full bg-white;

  :deep(.el-select-dropdown__item) {
    @apply px-3;
  }

  :deep(.el-select-dropdown) {
    @apply min-w-[300px] !important;
  }
}
</style>
